// ONBW1-S3 (docs/build-briefs/ONBW1-S3-owner-wizard.md; design/mockups/onboarding-wizard/x-*). The optional extras the location owner
// reaches from What is left: the receipt printer (a help request, never a form), cash, how guests get receipts, where guests
// tip, vendors and order days, and another device. Each is one screen, saves through the route Settings already calls, and
// returns to What is left. It reaches setupWizard.ts only through ExtraHost.
//
// Cash never tips (the CASH ruling): nothing here offers a tip on cash. No money is typed on the floor: the starting cash is the
// number pad of setupWizardUi, a setup value on an admin screen.

import {
  createVendor,
  getCashDrawerSettings,
  getReceiptSetting,
  getTipMode,
  issueTabletSetupCode,
  listVendors,
  postHelpRequest,
  putCashDrawerSettings,
  putCashEnabled,
  putReceiptSetting,
  putTipMode,
  putVendorOrderPace,
  type CashDrawerSettingsDto,
  type ReceiptModeKey,
  type VendorDto,
} from "./api.ts";
import {
  EXTRAS,
  LEAD_DAY_CHOICES,
  MAX_DRAWER_CENTS,
  ORDER_PACE_CHOICES,
  RECEIPT_CHOICES,
  TIP_WHERE_CHOICES,
  dollarText,
  padAppend,
  padDelete,
  type OwnerExtraId,
} from "./ownerWizardModel.ts";
import { hasPermission, type Session } from "./session.ts";
import {
  button,
  card,
  chipRow,
  el,
  field,
  pickCard,
  printerDrawing,
  tag,
  textInput,
} from "./setupWizardUi.ts";

export interface ExtraHost {
  session: Session;
  /** The location this device is tied to. */
  locationId: string | undefined;
  body: HTMLElement;
  live: () => boolean;
  column: (wide?: boolean) => HTMLElement;
  heading: (col: HTMLElement, kicker: string, title: string, hint?: string) => void;
  setPrimary: (label: string, enabled: boolean, reason: string, action: () => void) => void;
  setSecondary: (label: string, action: () => void) => void;
  setBack: (action: () => void) => void;
  showReason: (text: string) => void;
  runBusy: (action: () => Promise<void>) => Promise<void>;
  rerender: () => void;
  reviewed: (extra: OwnerExtraId) => boolean;
  /** Saves that the extra was reviewed (unless told not to) and returns to What is left. */
  finish: (extra: OwnerExtraId, options?: { review?: boolean }) => Promise<void>;
  back: () => void;
}

const KICKER = "Extra, optional";

// What the extras hold between redraws. Reset whenever the wizard leaves an extra for What is left.
let cashAnswer: "yes" | "no" | undefined;
let cashCents = 0;
let cashLoaded: CashDrawerSettingsDto | undefined;
let receiptChoice: ReceiptModeKey | undefined;
let receiptLoaded = false;
let tipChoice: "reader" | "receipt" | undefined;
let tipLoaded = false;
let printerAsked = false;
let vendorsList: VendorDto[] | undefined;
let vendorChoice: string | undefined;
let vendorName = "";
let paceChoice: number | undefined;
let leadChoice: number | undefined;
let deviceCode: { code: string; expiresAt: string } | undefined;
let deviceMaking = false;
let current: OwnerExtraId | undefined;

function resetFor(id: OwnerExtraId): void {
  if (current === id) return;
  current = id;
  cashAnswer = undefined;
  cashCents = 0;
  cashLoaded = undefined;
  receiptChoice = undefined;
  receiptLoaded = false;
  tipChoice = undefined;
  tipLoaded = false;
  printerAsked = false;
  vendorsList = undefined;
  vendorChoice = undefined;
  vendorName = "";
  paceChoice = undefined;
  leadChoice = undefined;
  deviceCode = undefined;
  deviceMaking = false;
}

/** Called when the wizard leaves the extras for good, so the next visit starts clean. */
export function resetExtras(): void {
  current = undefined;
}

export function renderExtraScreen(id: OwnerExtraId, host: ExtraHost): void {
  resetFor(id);
  host.setBack(() => {
    current = undefined;
    host.back();
  });
  switch (id) {
    case "printer":
      renderPrinter(host);
      break;
    case "cash":
      renderCash(host);
      break;
    case "receipts":
      renderReceipts(host);
      break;
    case "tipWhere":
      renderTipWhere(host);
      break;
    case "vendors":
      renderVendors(host);
      break;
    case "device":
      renderDevice(host);
      break;
  }
}

function titleOf(id: OwnerExtraId): string {
  return EXTRAS.find((extra) => extra.id === id)?.title ?? "";
}

function notNow(host: ExtraHost): void {
  host.setSecondary("Not now", () => {
    current = undefined;
    host.back();
  });
}

// ---- The receipt printer: ask for help ------------------------------------------------------------

function renderPrinter(host: ExtraHost): void {
  const col = host.column(true);
  host.heading(col, KICKER, titleOf("printer"), "We can finish this with you.");
  const canAsk = hasPermission(host.session, "admin.location_settings.manage");
  const asked = printerAsked || host.reviewed("printer");
  const ask = button(
    asked ? "Asked, Snip & Sip will help" : "Ask Snip & Sip for help",
    "ow-printer-help",
    asked ? "ow-btn ow-btn-wide ow-btn-on" : "ow-btn ow-btn-primary ow-btn-wide",
    () => void askForHelp(host),
  );
  ask.disabled = !canAsk || asked;
  col.append(
    el(
      "div",
      { cls: "ow-cols" },
      el("div", { cls: "ow-illus", testid: "ow-printer-drawing" }, printerDrawing()),
      el(
        "div",
        { cls: "ow-reader-right" },
        el(
          "div",
          { cls: "ow-steps" },
          stepLine(1, "Plug in the printer and join your Wi-Fi"),
          stepLine(2, "Tap Ask Snip & Sip for help"),
        ),
        ask,
        ...(asked ? [tag("Asked", "ok", "ow-printer-asked")] : []),
        el("div", { cls: "ow-error", testid: "ow-printer-error", attrs: { role: "alert" } }),
      ),
    ),
  );
  notNow(host);
  host.setPrimary("Continue", true, canAsk ? "" : "Not allowed", () => {
    void host.runBusy(async () => {
      current = undefined;
      await host.finish("printer", { review: printerAsked || host.reviewed("printer") });
    });
  });
}

function stepLine(n: number, text: string): HTMLElement {
  return el(
    "div",
    { cls: "ow-stepline" },
    el("span", { cls: "ow-stepnum", text: String(n) }),
    el("div", { cls: "ow-steptext" }, el("b", { text })),
  );
}

async function askForHelp(host: ExtraHost): Promise<void> {
  const session = host.session;
  await host.runBusy(async () => {
    const result = await postHelpRequest(session.apiBaseUrl, session.tenantId, "receipt_printer");
    if (!host.live()) return;
    if (result.status !== 200 && result.status !== 201) {
      const node = host.body.querySelector<HTMLElement>('[data-testid="ow-printer-error"]');
      if (node !== null)
        node.textContent = result.body.message ?? result.body.error ?? "Could not ask for help";
      return;
    }
    printerAsked = true;
    host.rerender();
  });
}

// ---- Cash -----------------------------------------------------------------------------------------

function renderCash(host: ExtraHost): void {
  const session = host.session;
  const locationId = host.locationId;
  if (
    cashLoaded === undefined &&
    locationId !== undefined &&
    hasPermission(session, "admin.tenant_settings.view")
  ) {
    void getCashDrawerSettings(session.apiBaseUrl, session.tenantId, locationId).then((result) => {
      if (!host.live() || result.status !== 200 || current !== "cash" || cashLoaded !== undefined)
        return;
      cashLoaded = result.body;
      cashCents = result.body.standardFloatCents;
      host.rerender();
    });
  }
  const canWrite = hasPermission(session, "admin.location_settings.manage");
  const col = host.column(cashAnswer === "yes");
  if (cashAnswer !== "yes") {
    host.heading(col, KICKER, titleOf("cash"));
    col.append(
      el(
        "div",
        { cls: "ow-picks", attrs: { role: "radiogroup", "aria-label": "Do you take cash" } },
        pickCard({
          testid: "ow-cash-yes",
          title: "Yes",
          chosen: false,
          glyph: "$",
          onPick: () => {
            cashAnswer = "yes";
            host.rerender();
          },
        }),
        pickCard({
          testid: "ow-cash-no",
          title: "No, cards only",
          chosen: cashAnswer === "no",
          glyph: "−",
          onPick: () => {
            cashAnswer = "no";
            host.rerender();
          },
        }),
      ),
    );
    notNow(host);
    host.setPrimary(
      "Save",
      canWrite && cashAnswer === "no" && locationId !== undefined,
      !canWrite ? "Not allowed" : cashAnswer === undefined ? "Pick one to continue" : "",
      () => void saveCash(host, false),
    );
    return;
  }
  host.heading(
    col,
    KICKER,
    "How much cash starts in the drawer?",
    "You count it at the start of each day.",
  );
  const value = el("div", {
    cls: "ow-input ow-padvalue",
    text: dollarText(cashCents),
    testid: "ow-cash-value",
    attrs: { "aria-live": "polite" },
  });
  const keys = el("div", { cls: "ow-keys", testid: "ow-cash-keys" });
  const key = (
    label: string,
    testid: string,
    fn: boolean,
    onTap: () => void,
  ): HTMLButtonElement => {
    const node = el("button", {
      cls: fn ? "ow-key ow-key-fn" : "ow-key",
      text: label,
      testid,
      attrs: { type: "button" },
    });
    node.addEventListener("click", onTap);
    return node;
  };
  for (const digit of ["1", "2", "3", "4", "5", "6", "7", "8", "9"]) {
    keys.append(
      key(digit, `ow-cash-digit-${digit}`, false, () => {
        cashCents = padAppend(cashCents, digit, MAX_DRAWER_CENTS);
        value.textContent = dollarText(cashCents);
      }),
    );
  }
  keys.append(
    key("Clear", "ow-cash-clear", true, () => {
      cashCents = 0;
      value.textContent = dollarText(cashCents);
    }),
    key("0", "ow-cash-digit-0", false, () => {
      cashCents = padAppend(cashCents, "0", MAX_DRAWER_CENTS);
      value.textContent = dollarText(cashCents);
    }),
    key("Delete", "ow-cash-delete", true, () => {
      cashCents = padDelete(cashCents);
      value.textContent = dollarText(cashCents);
    }),
  );
  col.append(
    el(
      "div",
      { cls: "ow-cols" },
      el("div", { cls: "ow-stack" }, field("Starting cash", value)),
      keys,
    ),
  );
  notNow(host);
  host.setPrimary(
    "Save",
    canWrite && locationId !== undefined,
    canWrite ? "" : "Not allowed",
    () => void saveCash(host, true),
  );
}

async function saveCash(host: ExtraHost, on: boolean): Promise<void> {
  const session = host.session;
  const locationId = host.locationId;
  if (locationId === undefined) return;
  await host.runBusy(async () => {
    const switched = await putCashEnabled(session.apiBaseUrl, session.tenantId, locationId, {
      cashEnabled: on,
    });
    if (!host.live()) return;
    if (switched.status !== 200) {
      host.showReason(switched.body.message ?? switched.body.error ?? "Could not save that");
      return;
    }
    if (on) {
      const figures = await putCashDrawerSettings(
        session.apiBaseUrl,
        session.tenantId,
        locationId,
        {
          standardFloatCents: cashCents,
          toleranceCents: switched.body.toleranceCents,
          paidOutApprovalCents: switched.body.paidOutApprovalCents,
        },
      );
      if (!host.live()) return;
      if (figures.status !== 200) {
        host.showReason(
          figures.body.message ?? figures.body.error ?? "Could not save the starting cash",
        );
        return;
      }
    }
    current = undefined;
    await host.finish("cash");
  });
}

// ---- Receipts -------------------------------------------------------------------------------------

function renderReceipts(host: ExtraHost): void {
  const session = host.session;
  const locationId = host.locationId;
  if (
    !receiptLoaded &&
    locationId !== undefined &&
    hasPermission(session, "admin.tenant_settings.view")
  ) {
    receiptLoaded = true;
    void getReceiptSetting(session.apiBaseUrl, session.tenantId, locationId).then((result) => {
      if (!host.live() || result.status !== 200 || current !== "receipts") return;
      if (receiptChoice === undefined) receiptChoice = result.body.mode;
      host.rerender();
    });
  }
  const col = host.column();
  host.heading(col, KICKER, titleOf("receipts"));
  const group = el("div", {
    cls: "ow-picks",
    attrs: { role: "radiogroup", "aria-label": "Receipts" },
  });
  for (const choice of RECEIPT_CHOICES) {
    const node = pickCard({
      testid: "ow-receipt-choice",
      title: choice.label,
      chosen: receiptChoice === choice.mode,
      glyph: "✉",
      onPick: () => {
        receiptChoice = choice.mode;
        host.rerender();
      },
    });
    node.dataset["mode"] = choice.mode;
    group.append(node);
  }
  col.append(group);
  notNow(host);
  const canWrite = hasPermission(session, "admin.location_settings.manage");
  host.setPrimary(
    "Save",
    canWrite && receiptChoice !== undefined && locationId !== undefined,
    canWrite ? (receiptChoice === undefined ? "Pick one to continue" : "") : "Not allowed",
    () => {
      const mode = receiptChoice;
      if (mode === undefined || locationId === undefined) return;
      void host.runBusy(async () => {
        const result = await putReceiptSetting(session.apiBaseUrl, session.tenantId, locationId, {
          mode,
        });
        if (!host.live()) return;
        if (result.status !== 200) {
          host.showReason(result.body.message ?? result.body.error ?? "Could not save that");
          return;
        }
        current = undefined;
        await host.finish("receipts");
      });
    },
  );
}

// ---- Where guests tip -----------------------------------------------------------------------------

function renderTipWhere(host: ExtraHost): void {
  const session = host.session;
  const locationId = host.locationId;
  if (
    !tipLoaded &&
    locationId !== undefined &&
    hasPermission(session, "admin.tenant_settings.view")
  ) {
    tipLoaded = true;
    void getTipMode(session.apiBaseUrl, session.tenantId, locationId).then((result) => {
      if (!host.live() || result.status !== 200 || current !== "tipWhere") return;
      if (tipChoice === undefined) tipChoice = result.body.mode;
      host.rerender();
    });
  }
  const col = host.column();
  host.heading(col, KICKER, titleOf("tipWhere"));
  const group = el("div", {
    cls: "ow-picks",
    attrs: { role: "radiogroup", "aria-label": "Where guests tip" },
  });
  for (const choice of TIP_WHERE_CHOICES) {
    const node = pickCard({
      testid: "ow-tipwhere-choice",
      title: choice.label,
      chosen: tipChoice === choice.mode,
      glyph: choice.mode === "reader" ? "□" : "☰",
      onPick: () => {
        tipChoice = choice.mode;
        host.rerender();
      },
    });
    node.dataset["mode"] = choice.mode;
    group.append(node);
  }
  col.append(group);
  notNow(host);
  const canWrite = hasPermission(session, "admin.location_settings.manage");
  host.setPrimary(
    "Save",
    canWrite && tipChoice !== undefined && locationId !== undefined,
    canWrite ? (tipChoice === undefined ? "Pick one to continue" : "") : "Not allowed",
    () => {
      const mode = tipChoice;
      if (mode === undefined || locationId === undefined) return;
      void host.runBusy(async () => {
        const result = await putTipMode(session.apiBaseUrl, session.tenantId, locationId, mode);
        if (!host.live()) return;
        if (result.status !== 200) {
          // The server says in plain words why paper tips are refused (a paperless receipt choice, a ticket still awaiting a tip).
          host.showReason(result.body.message ?? result.body.error ?? "Could not save that");
          return;
        }
        current = undefined;
        await host.finish("tipWhere");
      });
    },
  );
}

// ---- Vendors and order days -----------------------------------------------------------------------

const NEW_VENDOR = "new";

function renderVendors(host: ExtraHost): void {
  const session = host.session;
  if (vendorsList === undefined && hasPermission(session, "inv.vendor.view")) {
    vendorsList = [];
    void listVendors(session.apiBaseUrl, session.tenantId, { activeOnly: true }).then((result) => {
      if (!host.live() || current !== "vendors") return;
      vendorsList = result.status === 200 ? result.body.vendors : [];
      if (vendorChoice === undefined)
        vendorChoice = vendorsList.length === 0 ? NEW_VENDOR : undefined;
      host.rerender();
    });
  }
  const existing = vendorsList ?? [];
  if (vendorChoice === undefined && existing.length === 0 && vendorsList !== undefined)
    vendorChoice = NEW_VENDOR;
  const col = host.column();
  host.heading(col, KICKER, titleOf("vendors"), "Pick a vendor and the days you order.");
  const body = card("ow-vendor-card");
  if (existing.length > 0) {
    body.append(
      el(
        "div",
        { cls: "ow-field" },
        el("span", { cls: "ow-label", text: "Vendor" }),
        chipRow(
          "ow-vendor-chips",
          "ow-vendor-chip",
          [
            ...existing.map((vendor) => ({ value: vendor.id, label: vendor.name })),
            { value: NEW_VENDOR, label: "A new vendor" },
          ],
          vendorChoice ?? "",
          (value) => {
            vendorChoice = value;
            host.rerender();
          },
          "Vendor",
        ),
      ),
    );
  }
  if (vendorChoice === NEW_VENDOR) {
    const name = textInput({
      id: "ow-vendor-name",
      testid: "ow-vendor-name",
      value: vendorName,
      placeholder: "Vendor name",
    });
    name.addEventListener("input", () => {
      vendorName = name.value;
      refresh();
    });
    body.append(field("Vendor name", name));
  }
  body.append(
    el(
      "div",
      { cls: "ow-field" },
      el("span", { cls: "ow-label", text: "Order every" }),
      chipRow(
        "ow-pace-chips",
        "ow-pace-chip",
        ORDER_PACE_CHOICES.map((choice) => ({ value: String(choice.days), label: choice.label })),
        paceChoice === undefined ? "" : String(paceChoice),
        (value) => {
          paceChoice = Number(value);
          host.rerender();
        },
        "Order every",
      ),
    ),
    el(
      "div",
      { cls: "ow-field" },
      el("span", { cls: "ow-label", text: "Arrives in" }),
      chipRow(
        "ow-lead-chips",
        "ow-lead-chip",
        LEAD_DAY_CHOICES.map((choice) => ({ value: String(choice.days), label: choice.label })),
        leadChoice === undefined ? "" : String(leadChoice),
        (value) => {
          leadChoice = Number(value);
          host.rerender();
        },
        "Arrives in",
      ),
    ),
  );
  col.append(body);
  notNow(host);
  const canWrite = hasPermission(session, "inv.vendor.manage");
  const refresh = (): void => {
    const named =
      vendorChoice === NEW_VENDOR ? vendorName.trim() !== "" : vendorChoice !== undefined;
    const reason = !canWrite
      ? "Not allowed"
      : !named
        ? vendorChoice === NEW_VENDOR
          ? "Enter the vendor name"
          : "Pick a vendor"
        : paceChoice === undefined
          ? "Pick how often you order"
          : leadChoice === undefined
            ? "Pick when it arrives"
            : "";
    host.setPrimary("Save vendor", reason === "", reason, () => void saveVendor(host));
  };
  refresh();
}

async function saveVendor(host: ExtraHost): Promise<void> {
  const session = host.session;
  const pace = paceChoice;
  const lead = leadChoice;
  if (pace === undefined || lead === undefined) return;
  await host.runBusy(async () => {
    let vendorId = vendorChoice;
    if (vendorChoice === NEW_VENDOR) {
      const created = await createVendor(session.apiBaseUrl, session.tenantId, {
        name: vendorName.trim(),
      });
      if (!host.live()) return;
      if (created.status !== 201) {
        host.showReason(created.body.message ?? created.body.error ?? "Could not add the vendor");
        return;
      }
      vendorId = created.body.vendor.id;
    }
    if (vendorId === undefined) return;
    const result = await putVendorOrderPace(session.apiBaseUrl, session.tenantId, vendorId, {
      paceDays: pace,
      leadDays: lead,
    });
    if (!host.live()) return;
    if (result.status !== 200) {
      host.showReason(result.body.message ?? result.body.error ?? "Could not save the order days");
      return;
    }
    current = undefined;
    await host.finish("vendors");
  });
}

// ---- Another device -------------------------------------------------------------------------------

function timeText(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function renderDevice(host: ExtraHost): void {
  const session = host.session;
  const locationId = host.locationId;
  const canMake = hasPermission(session, "admin.device.manage") && locationId !== undefined;
  if (deviceCode === undefined && !deviceMaking && canMake) void makeCode(host);
  const minutes =
    deviceCode === undefined
      ? 0
      : Math.max(1, Math.round((new Date(deviceCode.expiresAt).getTime() - Date.now()) / 60000));
  const col = host.column();
  host.heading(
    col,
    KICKER,
    titleOf("device"),
    deviceCode === undefined
      ? undefined
      : `Enter this code on the new device. It works for ${minutes} minutes.`,
  );
  col.append(
    card(
      "ow-device-card",
      el("div", { cls: "ow-label", text: "Setup code" }),
      el("div", {
        cls: "ow-code",
        text: deviceCode?.code ?? (canMake ? "..." : "Not allowed"),
        testid: "ow-device-code",
      }),
      ...(deviceCode === undefined
        ? []
        : [
            el("div", {
              cls: "ow-sub",
              text: `Ends at ${timeText(deviceCode.expiresAt)}`,
              testid: "ow-device-ends",
            }),
          ]),
    ),
  );
  const copy = button("Copy code", "ow-device-copy", "ow-btn ow-btn-wide", () => {
    if (deviceCode === undefined) return;
    void navigator.clipboard?.writeText(deviceCode.code).catch(() => undefined);
  });
  copy.disabled = deviceCode === undefined;
  const again = button(
    "Make a new code",
    "ow-device-new",
    "ow-btn ow-btn-wide",
    () => void makeCode(host),
  );
  again.disabled = !canMake || deviceMaking;
  col.append(el("div", { cls: "ow-row" }, copy, again));
  host.setPrimary("Done", true, "", () => {
    current = undefined;
    host.back();
  });
}

async function makeCode(host: ExtraHost): Promise<void> {
  const session = host.session;
  const locationId = host.locationId;
  if (locationId === undefined || deviceMaking) return;
  deviceMaking = true;
  const result = await issueTabletSetupCode(session.apiBaseUrl, session.tenantId, {
    idempotencyKey: crypto.randomUUID(),
    locationId: locationId,
    displayName: "New device",
  });
  deviceMaking = false;
  if (!host.live() || current !== "device") return;
  if (result.status !== 201 && result.status !== 200) {
    host.showReason(result.body.message ?? result.body.error ?? "Could not make a code");
    return;
  }
  if (result.body.code !== null) {
    deviceCode = { code: result.body.code, expiresAt: result.body.setupCode.expiresAt };
  }
  host.rerender();
}
